/**
 * Main Application Controller
 * Orchestrates all modules and manages application lifecycle
 */

import { eventBus, isGuestName } from './core/utils.js';
import page from './vendor/page.js';
import { sessionManager } from './core/SessionManager.js';
import { Sidebar } from './ui/Sidebar.js';
import { Input } from './ui/Input.js';
import { PillRow } from './ui/PillRow.js';
import { PillDispatch } from './orchestration/PillDispatch.js';
import { AutocompleteService } from './services/AutocompleteService.js';
import { PageManager } from './ui/PageManager.js';
import { RightPanelManager } from './ui/RightPanelManager.js';
import { NotificationManager } from './chat/NotificationManager.js';
import { ChatManager } from './chat/ChatManager.js';
import { ResponseHandler } from './services/ResponseHandler.js';
import { ModeOrchestrator } from './orchestration/ModeOrchestrator.js';
import { HeaderManager } from './core/HeaderManager.js';
import { HeaderContentService } from './core/HeaderContentService.js';
import { DashboardManager } from './core/DashboardManager.js';
import { UpgradeSection } from './sections/UpgradeSection.js';
import { SubscriptionSection } from './sections/SubscriptionSection.js';
import { InsightsSection } from './sections/InsightsSection.js';
import { SessionsSection } from './sections/SessionsSection.js';
import { PracticesSection } from './sections/PracticesSection.js';
import { DiscoverySection } from './sections/DiscoverySection.js';
import { PartnerSection } from './sections/PartnerSection.js';
import { PartnerNotesSection } from './sections/PartnerNotesSection.js';
import { AccountSettingsSection } from './sections/AccountSettingsSection.js';
import { HelpSection } from './sections/HelpSection.js';
import { MemorySection } from './sections/MemorySection.js';
import { ActionButtons } from './components/ActionButtons.js';
import { ButtonGroupRegistry } from './core/ButtonGroupRegistry.js';
import { QuestionnaireLauncher } from './components/QuestionnaireLauncher.js';
import { PrivacyModeManager } from './components/PrivacyModeManager.js';
import { ChatHeaderMenu } from './components/ChatHeaderMenu.js';
import { PathProgressPopover } from './components/PathProgressPopover.js';
import { DiscoveryAPI } from './core/discoveryApi.js';
import { OrientationCloseManager } from './components/OrientationCloseManager.js';
import { ProfilePopup } from './components/ProfilePopup.js';
import { AuthManager } from './auth.js';
import { startLoginTypewriter, stopLoginTypewriter } from './utils/loginTypewriter.js';
import { initRouter } from './router.js';
import { BadgeService } from './services/BadgeService.js';
import { SidebarRecentsController } from './services/SidebarRecentsController.js';
import { FeatureToggleService } from './services/FeatureToggleService.js';
import { HeaderUpgradeController } from './services/HeaderUpgradeController.js';
import { UsageService } from './services/UsageService.js';
import { AnalyticsService, resolveAnalyticsTier } from './services/AnalyticsService.js';
import { helpHintService } from './services/HelpHintService.js';
import { AudioRecordingService } from './services/AudioRecordingService.js';
import { API_URL, API } from './core/api.js';
import { getSubscriptionSnapshot, planFromStatus, isPaidPlan, planName } from './core/subscriptionStatus.js';
import { createProfileAvatar } from './utils/profileAvatar.js';
import { isDevHost } from './utils/isDevHost.js';
import { installFileDropGuard } from './utils/fileDropGuard.js';
import { goToUpgrade } from './utils/upgradeNavigation.js';

let devToolsModule = null;
let devToolsInitialized = false;

/** Lazy-load the dev console on dev hosts only; never blocks and never throws. */
function loadDevTools() {
  if (!isDevHost()) return null;
  devToolsModule ??= import('./utils/devTools.js').catch((err) => {
    console.warn('[app] devTools failed to load', err);
    return null;
  });
  return devToolsModule;
}

/**
 * Initialize the lazy-loaded dev console at most once.
 *
 * @param {Object|null} module - Loaded dev-tools module
 * @param {Object} app - App instance exposed to the dev console
 */
function initializeDevTools(module, app) {
  if (!module || devToolsInitialized) return;
  devToolsInitialized = true;
  module.initDevTools(app);
}

/**
 * Determine if a user needs to complete orientation onboarding.
 * Single source of truth for this check - used by both app initialization and router.
 * Applies to ALL users (guests, SSO, and local accounts) who haven't completed onboarding.
 *
 * @param {Object} user - User object (may be null/undefined)
 * @returns {boolean} True if user hasn't completed orientation
 */
function needsOrientation(user) {
  // Check if onboardingStep exists and is not 'complete'
  // null/undefined means user needs orientation (new user)
  // 'complete' means orientation finished
  const step = user?.onboardingStep;
  if (step === 'complete') return false;
  // Dismissed users browse the dashboard freely; FeatureToggleService keeps
  // the rest of the app locked until orientation is actually complete.
  if (user?.orientationDismissedAt) return false;
  return true;
}

/**
 * Load SVG sprite from external file and inject into DOM.
 * Called early in initialization to ensure icons are available.
 */
async function loadSvgSprite() {
  try {
    const response = await fetch('/assets/icons/sprite.svg');
    if (!response.ok) {
      console.warn('[App] Failed to load SVG sprite:', response.status);
      return;
    }
    const svgContent = await response.text();

    // Create a hidden container for the sprite
    const spriteContainer = document.createElement('div');
    spriteContainer.style.display = 'none';
    spriteContainer.setAttribute('aria-hidden', 'true');
    spriteContainer.innerHTML = svgContent;

    // Insert at the beginning of body
    document.body.insertBefore(spriteContainer, document.body.firstChild);
    console.log('[App] SVG sprite loaded successfully');
  } catch (error) {
    console.warn('[App] Error loading SVG sprite:', error);
  }
}

/**
 * Overlay copy per conversion moment. The gate is deliberately placed where the
 * value has just been demonstrated, so the headline names that value rather than
 * asking generically to sign in. Keyed by the `context` passed to
 * `openConversionGate`; `default` covers the plain ACCOUNT_PROMPT ask.
 *
 * The SSO path is a full-page redirect and, on an account collision, signs the
 * user into their existing account WITHOUT merging guest data — so no line here
 * may promise carry-over unconditionally.
 */
const CONVERSION_OVERLAY_COPY = {
  conclusion_foundation: {
    title: 'Create your free account to start your journey',
    subtext: 'Everything from this conversation comes with you.',
    cancelLabel: 'Not right now'
  },
  conclusion_exit: {
    title: 'Create your free account to keep your profile',
    subtext: "Otherwise I'll hold what we've built — your insight, this conversation, your experiment — for about 7 days.",
    cancelLabel: "I'm done for now"
  },
  offtopic_expand: {
    title: 'Create your free account to keep going',
    subtext: 'This conversation can open up beyond what we started with once you have somewhere to keep it.',
    cancelLabel: 'Not right now'
  },
  transition_foundation: {
    title: 'Create your free account to start your journey',
    subtext: 'Your profile is already started — an account is what keeps it.',
    cancelLabel: 'Not right now'
  },
  dashboard: {
    title: 'Create your free account',
    subtext: 'Your profile is started — an account keeps it and opens the rest.',
    cancelLabel: 'Not right now'
  },
  guest_message_limit: {
    title: 'Create your free account to keep talking',
    subtext: "That's as far as this preview goes. Your account picks up right here.",
    cancelLabel: 'Not right now'
  },
  default: {
    title: 'Create your free account',
    subtext: 'Keep what you have built here.',
    cancelLabel: 'Not right now'
  }
};

class App {
  constructor() {
    this.currentUser = null;
    this.isGuest = false; // Track if current user is a guest
    this.initialized = false; // Track if app has been fully initialized
    this.modules = {
      authManager: null // AuthManager for login overlay
    };
    this.services = {}; // Service instances (autocomplete, etc.)
    this.pendingNavigation = null; // Router coordination state
    this.returnSession = null; // Track session for restoration on return from dashboard
    this.checkoutReturnSession = null; // Post-Stripe-checkout resume pointer (populated before initRouter)
    // The action a guest clicked that opened the conversion overlay, so it can
    // be continued on success or restored on dismissal. Lives at app level
    // because the gate fires from both ADVICE (the demo's conclusion) and
    // ORIENTATION (the skipped path's TRANSITION). See openConversionGate.
    this._conversionIntent = null;
    // An account created before the app had any listeners for it. See
    // noteAccountCreated / _flushPendingAccountCreated.
    this._pendingAccountCreated = null;

    // Check authentication before initializing app
    this.checkAuth();
  }

  /**
   * Check if user is authenticated
   * Checks for both authenticated users and existing guest sessions
   * Shows login overlay if neither exists, or initializes app if found
   */
  async checkAuth() {
    // Show loading state
    document.querySelector('.app')?.setAttribute('data-auth-loading', 'true');

    await this._exchangeBetaAccessFragment();

    // Outcomes the Google callback reports back on the redirect
    // (`backend/src/routes/auth.js` GET /auth/google/callback). Each is acted on
    // once, then the param is stripped: left in the URL it would replay on every
    // reload — for a signup that means counting one account many times — and
    // travel into any link the user shares from the address bar.
    const authQuery = new URLSearchParams(window.location.search).get('auth');
    let authQueryHandled = false;

    if (authQuery === 'account_exists') {
      // A guest→SSO conversion collided with an existing account. Set a pending
      // flag to surface the message once the overlay is up.
      this._pendingSSOError = 'account_exists';
      authQueryHandled = true;
    } else if (authQuery === 'beta_access_required') {
      this._pendingLoginMessage =
        'Your beta authorization is missing or no longer valid. Enter an access code to create an account.';
      authQueryHandled = true;
    } else if (authQuery === 'created' || authQuery === 'converted') {
      // A Google signup ('converted' = a guest who signed up with Google). This
      // param is the only thing that distinguishes it from a returning sign-in:
      // both come back to the same URL and /auth/me reports no "just created"
      // signal, only `createdAt`, whose recency is a guess rather than a fact.
      // The emit itself waits until the user is loaded, below.
      authQueryHandled = true;
    }

    if (authQueryHandled) {
      const url = new URL(window.location.href);
      url.searchParams.delete('auth');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }

    try {
      // Parallelize both auth checks - eliminates sequential latency
      const [authResult, guestStatus] = await Promise.all([
        API.checkAuth().catch(() => null),
        API.getGuestStatus().catch(() => null)
      ]);

      if (authResult?.user) {
        // User is authenticated (or guest with session via /auth/me)
        this.currentUser = authResult.user;
        this.isGuest = authResult.user.isGuest || false;
        helpHintService.hydrate(authResult.user.helpHints);

        // The OAuth signup the query param announced. Recorded here rather than
        // where the param was read because the payload wants the user, and
        // before the partner-invite gate below because that gate returns without
        // reaching initialize() — buffering now means the consent screen's
        // accept/decline still releases the event when the user resumes.
        if (authQuery === 'created' || authQuery === 'converted') {
          this.noteAccountCreated({
            user: authResult.user,
            method: 'google',
            fromConversion: authQuery === 'converted'
          });
        }

        // Partner-invite post-auth gate — e.g., Google SSO callback ends up
        // here, not via auth.js's verify-code success path. /auth/me has
        // already cleared the session for self-accept, so this branch only
        // fires for legitimate invitees.
        if (!this.isGuest && authResult.hasPendingInvite) {
          await this._showPostAuthConsent({
            inviterName: authResult.pendingInviteInviter?.firstName,
            inviterPhoto: authResult.pendingInviteInviter?.photoUrl,
            token: authResult.pendingInviteToken,
            partnerRateAvailable: authResult.partnerRateAvailable
          });
          return;
        }

        const orientationPending = this._setupOrientationIfNeeded(authResult.user);

        console.log('[App] User authenticated:', this.currentUser.username,
          this.isGuest ? '(guest)' : '',
          orientationPending ? '(orientation pending)' : '');
        this.initialize();

        // Guest→SSO collision fall-through: the backend signed the user into
        // an existing account because their Google identity was already taken.
        // Surface a toast so they know their guest session's progress isn't
        // carried over.
        if (this._pendingSSOError === 'account_exists') {
          this._pendingSSOError = null;
          window.notificationManager?.showInfo(
            "Signed in to your existing account. Any progress from this session wasn't carried over.",
            8000
          );
        }
      } else if (guestStatus?.isGuest) {
        // Existing guest session found (no authenticated session)
        console.log('[App] Guest status received:', {
          onboardingStep: guestStatus.onboardingStep,
          onboardingData: guestStatus.onboardingData,
          firstName: guestStatus.firstName
        });
        this.currentUser = {
          id: guestStatus.userId,
          username: `guest_${guestStatus.userId.slice(0, 8)}`,
          firstName: guestStatus.firstName,
          isGuest: true,
          onboardingStep: guestStatus.onboardingStep || null,
          onboardingData: guestStatus.onboardingData || {}
        };
        this.isGuest = true;
        helpHintService.hydrate(guestStatus.helpHints);
        const orientationPending = this._setupOrientationIfNeeded(this.currentUser);

        console.log('[App] Guest session found, initializing',
          orientationPending ? '(orientation pending)' : '');
        this.initialize();

        // No overlay is shown on this path, so a rejected access code has to be
        // explained as a toast or not at all.
        const pendingMessage = this._consumePendingLoginMessage();
        if (pendingMessage) {
          window.notificationManager?.showError(pendingMessage, 8000);
        }
      } else {
        // Check if user is accessing /try-free (guest entry point)
        if (window.location.pathname === '/try-free' && !this._betaFragmentRejected) {
          console.log('[App] /try-free route detected, initializing as guest');
          await this.initializeAsGuest();
          return;
        }
        // No session - show login overlay
        console.log('[App] User not authenticated, showing login');
        this.showLoginOverlay();
      }
    } catch (error) {
      console.error('[App] Auth check error:', error);
      // On error, show login overlay
      this.showLoginOverlay();
    } finally {
      // Remove loading state
      document.querySelector('.app')?.removeAttribute('data-auth-loading');
    }
  }

  /**
   * Exchange `/signup#access=...` without ever sending the secret in a URL.
   * Removes the fragment before any other navigation or analytics work.
   */
  async _exchangeBetaAccessFragment() {
    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const code = fragment.get('access');
    if (!code) return;

    fragment.delete('access');
    const cleanHash = fragment.toString() ? `#${fragment.toString()}` : '';
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}${cleanHash}`);

    const result = await API.validateBetaAccess(code);
    if (result?.authorized) {
      this._betaAuthorized = true;
      return;
    }
    this._betaFragmentRejected = true;
    this._pendingLoginMessage = result?.error || 'That beta access code is not valid.';
  }

  /**
   * Show the Partner-B consent screen post-auth. Shared by the SSO-callback
   * `/auth/me` branch and any other authenticated entry that needs to gate
   * on `req.session.pendingInviteToken`.
   *
   * Reuses the login-overlay surface for visual continuity with the email-
   * verify path. AuthManager is lazy-instantiated (users who already had a
   * session never constructed it) — mirror the dev-trigger pattern.
   *
   * @param {{ inviterName?: string, inviterPhoto?: string|null, token: string,
   *   partnerRateAvailable?: boolean }} options
   */
  async _showPostAuthConsent({ inviterName, inviterPhoto, token, partnerRateAvailable }) {
    // Reuses the login-overlay surface and never calls initialize(), so dismiss
    // the splash here too — otherwise it lingers until the 8s safety timeout.
    this.dismissSplash();

    const overlay = document.getElementById('login-overlay');
    const appContainer = document.querySelector('.app');
    if (overlay) overlay.hidden = false;
    if (appContainer) appContainer.hidden = true;

    if (!this.modules.authManager) {
      this.modules.authManager = new AuthManager(this);
    }
    await this.modules.authManager.initialize();
    this.modules.authManager.showConsentScreen({
      inviterName,
      inviterPhoto,
      token,
      partnerRateAvailable
    });

    // Expose app + dev tools for this login-overlay state.
    window.app = this;
    loadDevTools()?.then((module) => initializeDevTools(module, this));
  }

  /**
   * Read and clear the beta-access message captured before we knew which auth
   * path this load would take (`#access=` exchange, `?auth=beta_access_required`).
   *
   * It used to be surfaced only by `showLoginOverlay()`. A visitor who already
   * holds a guest session, or who reaches the conversion overlay, never passes
   * through there — so the one explanation of *why* their access was rejected
   * was captured and silently dropped.
   *
   * @returns {string|null} The pending message, or null if there is none
   */
  _consumePendingLoginMessage() {
    const message = this._pendingLoginMessage || null;
    this._pendingLoginMessage = null;
    return message;
  }

  /**
   * Show login overlay and hide app
   */
  showLoginOverlay() {
    console.log('[App] Showing login overlay');

    // Dismiss the splash with the same 600ms minimum the authenticated path uses,
    // so the login screen's splash feels identical to the rest of the app rather
    // than flashing and vanishing.
    this.dismissSplash(600);

    const loginOverlay = document.getElementById('login-overlay');
    const appContainer = document.querySelector('.app');

    if (loginOverlay) {
      loginOverlay.hidden = false;

      // Initialize auth manager if not already done
      if (!this.modules.authManager) {
        this.modules.authManager = new AuthManager(this);
      }
      const initialization = this.modules.authManager.initialize();

      Promise.resolve(initialization).then(() => {
        const msg = this._consumePendingLoginMessage();
        if (msg) this.modules.authManager.showBetaAccessPrompt(msg);
      });

      // Start the typewriter animation in the decor pane
      startLoginTypewriter();
    }

    if (appContainer) {
      appContainer.hidden = true;
    }

    // Update sidebar profile button to signed-out state
    this.updateSidebarProfileButton();

    // Expose app + dev tools so the login-overlay state is inspectable too.
    // (The authenticated path calls these in initialize(); mirror here so
    // pre-login states aren't a dead zone for console debugging.)
    window.app = this;
    loadDevTools()?.then((module) => initializeDevTools(module, this));
  }

  /**
   * Hide login overlay and show app
   */
  hideLoginOverlay() {
    console.log('[App] Hiding login overlay');

    // Safety net: clear conversion flag if still set (covers edge cases)
    if (this._isConvertingGuestToAccount) {
      this._isConvertingGuestToAccount = false;
    }

    const loginOverlay = document.getElementById('login-overlay');
    const appContainer = document.querySelector('.app');

    if (loginOverlay) {
      loginOverlay.hidden = true;
      stopLoginTypewriter();
    }

    if (appContainer) {
      appContainer.hidden = false;
    }
  }

  /**
   * Callback called by AuthManager after successful login/register
   */
  onLoginSuccess() {
    console.log('[App] Login successful, initializing app');

    // Sign-in happens in place with no page reload, so this is the only chance
    // to seed hint state for the whole visit — /auth/me is never refetched.
    // Reached from email/password login, registration that is NOT a guest
    // conversion, and partner-invite consent accept/decline.
    helpHintService.hydrate(this.currentUser?.helpHints);

    this.hideLoginOverlay();

    const orientationPending = this._setupOrientationIfNeeded(this.currentUser);
    if (orientationPending) {
      console.log('[App] Orientation pending after login');
    }

    this.initialize();
  }

  /**
   * Record that the server just created an account, and emit
   * `auth:account-created` once something is listening for it.
   *
   * Deliberately NOT wired into `onLoginSuccess()`: that runs for ordinary
   * sign-ins of existing users too (and for partner-consent accept/decline), so
   * emitting there would report every returning login as a new account and
   * corrupt the conversion metric. Creation is only knowable where the server
   * says it happened — the registration response in `auth.js`, or
   * `handleGuestConversion` below.
   *
   * The buffering exists because AnalyticsService is constructed inside
   * `initialize()`, and a cold visitor who registers from the login overlay has
   * not reached it yet. Emitting straight away would fire into an event bus with
   * no subscriber and the conversion event would simply vanish. A converting
   * guest is already initialized, so their emit goes out immediately — as it
   * always has, since OrientationMode's continuation depends on it.
   *
   * @param {Object} options
   * @param {Object} options.user - The newly created user
   * @param {'email'|'google'} [options.method='email'] - How the account was created
   * @param {boolean} [options.fromConversion=false] - Whether this converted a guest
   */
  noteAccountCreated({ user, method = 'email', fromConversion = false }) {
    const payload = { user, method, fromConversion };

    // `services.analytics` is the subscriber this event exists for, so its
    // presence is the precise test for "is anyone listening yet".
    if (this.services.analytics) {
      eventBus.emit('auth:account-created', payload);
      return;
    }

    this._pendingAccountCreated = payload;
  }

  /**
   * Emit the account creation `noteAccountCreated` held back while the app had
   * no listeners. Consumed on read: `initialize()` can run more than once in a
   * page load and a conversion event must never be counted twice.
   *
   * Note the one gap this leaves: a registration that stops at the partner
   * consent screen and is abandoned there never reaches `initialize()`, so the
   * account exists but the event is never sent. Accepting or declining — what
   * every consenting user does next — flushes it.
   *
   * @private
   */
  _flushPendingAccountCreated() {
    const pending = this._pendingAccountCreated;
    if (!pending) return;
    this._pendingAccountCreated = null;
    eventBus.emit('auth:account-created', pending);
  }

  /**
   * Initialize the app as a guest user
   * Called from /try-free route or "Try free" button
   * Creates a guest session and enters the app without login
   */
  async initializeAsGuest() {
    console.log('[App] Initializing as guest');

    // If already initialized as guest, just hide the overlay and return
    // This handles the case where a guest navigates to /login and clicks "Try free"
    if (this.initialized && this.isGuest) {
      console.log('[App] Already initialized as guest, hiding overlay');
      const wasConverting = this._isConvertingGuestToAccount;
      this._isConvertingGuestToAccount = false;
      this.hideLoginOverlay();

      if (wasConverting) {
        // A gated action owns its own dismissal semantics (restore the group,
        // or proceed with the exit). Only the ungated ACCOUNT_PROMPT ask falls
        // through to the broadcast, which OrientationMode answers by rendering
        // the guest-continues beat.
        const handled = await this._resolveDismissedConversionIntent();
        if (!handled) eventBus.emit('auth:conversion-dismissed');
      }
      return;
    }

    try {
      // Create/get guest session
      const guestStatus = await API.initGuestSession();

      if (!guestStatus || !guestStatus.isGuest) {
        throw new Error('Failed to create guest session');
      }

      // Set up current user as guest
      this.currentUser = {
        id: guestStatus.userId,
        username: `guest_${guestStatus.userId.slice(0, 8)}`,
        firstName: guestStatus.firstName,
        isGuest: true,
        onboardingStep: guestStatus.onboardingStep || null,
        onboardingData: guestStatus.onboardingData || {}
      };
      this.isGuest = true;
      // A freshly created guest is default state by construction, but reset
      // explicitly so this doesn't inherit whatever a prior account left behind
      // when /try-free is entered after a logout in the same page load.
      helpHintService.reset();

      // Set up orientation if needed (uses same logic as other paths)
      this._setupOrientationIfNeeded(this.currentUser);

      console.log('[App] Guest session created:', this.currentUser.id,
        this._pendingOrientation ? '(orientation pending)' : '');

      // Hide login overlay and show app
      this.hideLoginOverlay();

      // Initialize the full app
      this.initialize();

    } catch (error) {
      console.error('[App] Guest initialization failed:', error);
      throw error; // Re-throw so caller can handle
    }
  }

  /**
   * Dismiss the loading splash: clear its timers, fade, and remove the element.
   *
   * `minDisplayMs` enforces a floor so a fast load doesn't flash-and-vanish.
   * Both the authenticated/guest path and the login overlay pass 600ms so the
   * splash feels consistent everywhere; the post-auth consent screen passes 0
   * (it dismisses the instant it takes over the overlay). No-op in ?splash=hold
   * dev mode (dismiss manually with window.dismissAppSplash()).
   *
   * @param {number} [minDisplayMs=0] Minimum splash display time before fading.
   */
  dismissSplash(minDisplayMs = 0) {
    if (window.__appSplashHold) return;
    const elapsed = Date.now() - (window.__appSplashStart || 0);
    const remaining = Math.max(0, minDisplayMs - elapsed);

    setTimeout(() => {
      if (window.__appSplashTimeout) {
        clearTimeout(window.__appSplashTimeout);
        window.__appSplashTimeout = null;
      }
      if (window.__appSplashSlowTimer) {
        clearTimeout(window.__appSplashSlowTimer);
        window.__appSplashSlowTimer = null;
      }
      const splash = document.getElementById('app-splash');
      if (!splash) return;
      splash.classList.add('app-splash--fading');
      splash.addEventListener('transitionend', () => splash.remove(), { once: true });
      setTimeout(() => splash.remove(), 600);
    }, remaining);
  }

  /**
   * Initialize the application after authentication is confirmed
   */
  initialize() {
    // Set window.eventBus early so modules can register listeners during init
    window.eventBus = eventBus;

    this.initializeElements();
    this.initializeSessionManager();
    this.initializeModules();
    this.initializeComponents();

    // Initialize navigation click handlers
    this.initializeNavigation();

    // Populate this.checkoutReturnSession BEFORE initRouter: initRouter fires
    // the current route synchronously at the end, and handleBaseRoute's
    // Priority 1.5 branch depends on this.checkoutReturnSession being set.
    this._rehydrateCheckoutReturnSession();

    // Expose `window.app` BEFORE initRouter. initRouter ends with `page()`,
    // which dispatches the current route synchronously up to the handler's
    // first await — and any code that runs in that prefix (or any await
    // resumed before the rest of `init` finishes) may read `window.app`.
    // Setting it here closes that window.
    window.app = this;

    // Sidebar Recents: instantiated after `window.app` is
    // set (not from the earlier initializeModules() slot the dead
    // RecentChatsService init used to live in) — the notes source's couple
    // gate reads `window.app?.currentUser?.coupleId` synchronously inside
    // `load()`, so initializing any earlier would always resolve as
    // "uncoupled," silently dropping partner notes from the sidebar for every
    // coupled user.
    this.modules.sidebarRecents = new SidebarRecentsController(
      document.getElementById('sidebar-recents-sections'),
      { userId: this.currentUser?.id }
    );
    this.modules.sidebarRecents.initialize();

    // Initialize router (must be after modules and navigation)
    initRouter(this);

    this.displayUserInfo();
    this.initializeAuth();

    // Router processes current URL automatically via page() call
    // Direct navigation to /library and /discovery works
    // Direct navigation to /chat redirects to dashboard (handled in router)

    // Reveal app with fade-in (prevents flash of unstyled content)
    document.querySelector('.app')?.classList.add('app--ready');

    // Dismiss the splash screen — hold a brief minimum so real content booting
    // behind it doesn't flash-and-vanish.
    this.dismissSplash(600);

    // Mark as initialized to prevent double initialization
    this.initialized = true;

    // Every eventBus subscriber now exists, so a cold signup's account-created
    // event finally has somewhere to land. Last thing before dev tools so the
    // emit sees the fully wired app, not a half-built one.
    this._flushPendingAccountCreated();

    // Initialize dev tools (localhost only)
    loadDevTools()?.then((module) => initializeDevTools(module, this));

    console.log('✅ App initialized. Access via window.app and window.eventBus');

    // Route to orientation if pending (guest with incomplete onboarding)
    if (this._pendingOrientation) {
      this.routeToOrientation(this._pendingOrientation);
      this._pendingOrientation = null;
    }
  }

  /**
   * Check if user needs orientation and set up pending orientation if so.
   * Consolidates orientation setup logic - called from all auth paths.
   *
   * @param {Object} user - User object with onboardingStep, onboardingData, firstName
   * @returns {boolean} True if orientation was set up
   */
  _setupOrientationIfNeeded(user) {
    if (!needsOrientation(user)) {
      console.log('[App] User does not need orientation:', { onboardingStep: user?.onboardingStep });
      return false;
    }

    // Build onboardingData, including firstName from user profile (for SSO users)
    const onboardingData = user.onboardingData || {};
    if (user.firstName && !onboardingData.firstName) {
      onboardingData.firstName = user.firstName;
    }

    this._pendingOrientation = {
      resumeFrom: user.onboardingStep || null,
      onboardingData
    };

    console.log('[App] Orientation pending set:', this._pendingOrientation);

    return true;
  }

  /**
   * Route to OrientationMode for onboarding flow
   *
   * @param {Object} orientationData - Orientation state
   * @param {string|null} orientationData.resumeFrom - Step to resume from (null for new)
   * @param {Object} orientationData.onboardingData - Accumulated onboarding data
   */
  async routeToOrientation(orientationData) {
    console.log('[App] Routing to OrientationMode:', orientationData);

    // Ensure we're on the chat page
    if (this.modules.pageManager) {
      this.modules.pageManager.showPage('chat');
    }

    // Update URL to reflect orientation state
    window.history.replaceState({}, '', '/chat');

    // Switch to OrientationMode
    if (this.modules.modeOrchestrator) {
      await this.modules.modeOrchestrator.switchMode('ORIENTATION', {
        resumeFrom: orientationData.resumeFrom,
        onboardingData: orientationData.onboardingData
      });
    }
  }

  /**
   * Initialize session manager
   * Exposes sessionManager to window for debugging and backward compatibility
   */
  initializeSessionManager() {
    // Make sessionManager available globally for debugging
    window.sessionManager = sessionManager;
    console.log('SessionManager initialized');

    // Note: Sessions are created lazily on first message send
    // This allows the app to load quickly without blocking on session creation
  }

  /**
   * Initialize DOM element references
   */
  initializeElements() {
    // Sidebar elements
    this.elements = {
      sidebar: document.getElementById('sidebar'),
      sidebarToggle: document.getElementById('sidebar-toggle'),
      sidebarBackdrop: document.getElementById('sidebar-backdrop'),

      // Page container elements
      pageContainer: document.getElementById('page-container'),
      dashboard: document.getElementById('dashboard'),

      // Main wrapper (contains main and right panel)
      mainWrapper: document.getElementById('main-wrapper'),

      // Right panel elements
      rightPanel: document.getElementById('right-panel'),
      rightPanelClose: document.getElementById('right-panel-close'),
      rightPanelBack: document.getElementById('right-panel-back'),
      rightPanelTitle: document.getElementById('right-panel-title'),
      rightPanelContent: document.getElementById('right-panel-content'),
      rightPanelStar: document.getElementById('right-panel-star'),
      rightPanelOptionsMount: document.getElementById('right-panel-options-mount'),
      rightPanelMenuMount: document.getElementById('right-panel-menu-mount'),
      rightPanelSecondary: document.getElementById('right-panel-secondary'),
      rightPanelPrimary: document.getElementById('right-panel-primary'),
      resizeHandle: document.getElementById('resize-handle'),
      chatContainer: document.getElementById('chat-container'),

      // Input area elements (glassmorphism design)
      chatInput: document.getElementById('chat-input'),
      submitButton: document.getElementById('submit-button'),
      micButton: document.getElementById('mic-button'),
      inputDisabledOverlay: document.getElementById('input-disabled-overlay'),
      autocompleteContainer: document.getElementById('input-autocomplete'),
      autocompleteList: document.getElementById('input-autocomplete-list'),
      plusButton: document.getElementById('input-plus-button'),

      // Chat content elements
      chatContent: document.getElementById('chat-content'),
      messages: document.getElementById('messages'),
      messagesList: document.getElementById('messages-list')
    };
  }

  /**
   * Initialize all application modules
   */
  initializeModules() {
    // Initialize Sidebar
    this.modules.sidebar = new Sidebar({
      sidebar: this.elements.sidebar,
      sidebarToggle: this.elements.sidebarToggle,
      sidebarBackdrop: this.elements.sidebarBackdrop,
      userId: this.currentUser?.id
    });
    window.sidebar = this.modules.sidebar;

    // Notes is couple-gated, but the content resolver (FeatureToggleService,
    // `_resolveSidebarContent`) now owns its visibility — it reveals Notes for
    // linked users when orientation is not live, on init and every re-resolve.
    // (Linking completes server-side, so a fresh bootstrap picks up the new
    // coupleId — there is deliberately no live partner:linked reveal.)
    // Live unlink: nothing else clears the stale coupleId, so the couple
    // gates (sidebar item, /notes activate redirect) would otherwise
    // only fire on the next full load. Sidebar owns its own hide + redirect.
    eventBus.on('partner:unlinked', () => {
      if (this.currentUser) this.currentUser.coupleId = null;
    });

    // Initialize BadgeService (centralized badge state management)
    this.modules.badgeService = new BadgeService(
      eventBus,
      this.currentUser?.id,
      this.currentUser?.coupleId
    );
    // Initialize async - don't block app startup
    this.modules.badgeService.initialize();
    // Make globally accessible for debugging and component access
    window.badgeService = this.modules.badgeService;

    // Initialize AutocompleteService (load suggestions async)
    this.services.autocomplete = new AutocompleteService();
    this.services.autocomplete.loadSuggestions(); // Non-blocking load

    // Initialize Input with autocomplete
    this.modules.input = new Input({
      chatInput: this.elements.chatInput,
      submitButton: this.elements.submitButton,
      micButton: this.elements.micButton,
      inputMenuButton: this.elements.inputMenuButton,
      inputMenu: this.elements.inputMenu,
      inputMenuPanel: this.elements.inputMenuPanel,
      inputDisabledOverlay: this.elements.inputDisabledOverlay,
      autocompleteContainer: this.elements.autocompleteContainer,
      autocompleteList: this.elements.autocompleteList,
      plusButton: this.elements.plusButton
    }, eventBus, this.services.autocomplete);

    // Initialize audio recording service
    this.services.audioRecording = new AudioRecordingService(eventBus);
    this.modules.input.setAudioService(this.services.audioRecording);

    // Initialize the PillRow (floating pill buttons above the chat input). Phase 2
    // registers a single Tier-1 tenant: the "Back to buttons" pill, shown whenever a
    // button group is stashed behind the textarea via "respond in your own words".
    // Suppressed mid-detour over a stashed DURABLE-proposal group (app-tools plan
    // Phase 3.5, decision #8): the stash survives as a state carrier, but the pill
    // itself hides so the input looks completely normal while the user is talking
    // about something else.
    this.modules.pillRow = new PillRow(eventBus, this.modules.input);
    this.modules.pillRow.register({
      id: 'back-to-buttons',
      label: 'Back to buttons',
      tier: 1,
      visibleWhen: () => this.modules.input.hasStashedButtons()
        && !this.modules.input.isStashedButtonsPillSuppressed(),
      onClick: () => this.modules.input.restoreStashedButtons()
    });
    // Usage-lock "Upgrade" CTA — rides the pill row above the input (dark, high-emphasis
    // styling via `.chat-input__upgrade-pill`, not the suggestion-pill look). Tier-1 so
    // it preempts any agent pills during a hard lock. Gated on the usage overlay being
    // active; PillRow's composer-state gate keeps it off the dashboard.
    this.modules.pillRow.register({
      id: 'usage-upgrade',
      label: 'Upgrade for unlimited',
      tier: 1,
      className: 'chat-input__upgrade-pill',
      visibleWhen: () => !!this.services?.usage?.isLockActive?.(),
      onClick: () => goToUpgrade(eventBus, 'upgradePill')
    });

    // Queue-until-idle contract for pill clicks during streaming (single pending
    // slot, placeholder swap, fire on chat:idle) — agent-pill onClicks route through
    // this via ActionRouter._buildAgentPill. Design: pill-streaming-interaction-design.md.
    this.modules.pillDispatch = new PillDispatch({ eventBus });

    // Initialize Page Manager
    this.modules.pageManager = new PageManager({
      pageContainer: this.elements.pageContainer,
      dashboard: this.elements.dashboard
    }, eventBus);

    // Initialize Right Panel Manager
    this.modules.rightPanelManager = new RightPanelManager({
      rightPanel: this.elements.rightPanel,
      rightPanelClose: this.elements.rightPanelClose,
      rightPanelBack: this.elements.rightPanelBack,
      rightPanelTitle: this.elements.rightPanelTitle,
      rightPanelContent: this.elements.rightPanelContent,
      rightPanelStar: this.elements.rightPanelStar,
      rightPanelOptionsMount: this.elements.rightPanelOptionsMount,
      rightPanelMenuMount: this.elements.rightPanelMenuMount,
      rightPanelSecondary: this.elements.rightPanelSecondary,
      rightPanelPrimary: this.elements.rightPanelPrimary,
      resizeHandle: this.elements.resizeHandle,
      mainWrapper: this.elements.mainWrapper,
      chatContainer: this.elements.chatContainer
    }, eventBus);

    // Initialize Notification Manager
    this.modules.notificationManager = new NotificationManager(
      document.getElementById('notification-toast')
    );

    // Make notification manager globally available for easy access
    window.notificationManager = this.modules.notificationManager;

    // Initialize Chat Manager
    this.modules.chatManager = new ChatManager({
      chatContent: this.elements.chatContent,
      messages: this.elements.messages,
      messagesList: this.elements.messagesList
    }, eventBus);

    // Wire ChatManager to Input for stop functionality
    this.modules.input.setChatManager(this.modules.chatManager);

    // Initialize ResponseHandler for unified response format
    this.modules.responseHandler = new ResponseHandler(
      this.modules.chatManager,
      eventBus
    );
    // Make available globally for flow clients
    window.responseHandler = this.modules.responseHandler;

    // Set up component event handlers for unified response pipeline
    this.initializeComponentHandlers();

    // Initialize HeaderManager first (before ModeOrchestrator)
    const headerElement = document.getElementById('mode-header');
    if (headerElement) {
      this.modules.headerManager = new HeaderManager(
        headerElement,
        null, // ModeOrchestrator reference will be set after creation
        eventBus
      );
      // Don't initialize yet - will be done after HeaderContentService is created
    } else {
      console.warn('Header element not found - HeaderManager not initialized');
    }

    // Initialize HeaderContentService (requires HeaderManager)
    if (this.modules.headerManager) {
      this.modules.headerContentService = new HeaderContentService(
        this.modules.headerManager,
        eventBus
      );
      console.log('✅ HeaderContentService initialized');
    }

    // Initialize ModeOrchestrator (requires HeaderContentService)
    // Note: No sessionId needed - retrieved dynamically from SessionManager
    this.modules.modeOrchestrator = new ModeOrchestrator(
      this.modules.chatManager, // Pass chatManager, not messageList
      eventBus,
      this.currentUser?.id, // Get from auth
      this.currentUser?.coupleId, // Get from auth
      this.modules.headerContentService // Pass HeaderContentService
    );
    // Expose globally - OrientationMode and other components access eventBus via window.modeOrchestrator
    window.modeOrchestrator = this.modules.modeOrchestrator;

    // Initialize FeatureToggleService for onboarding feature control
    this.services.featureToggle = new FeatureToggleService(eventBus);
    this.services.featureToggle.initialize();
    window.featureToggleService = this.services.featureToggle;
    console.log('✅ FeatureToggleService initialized');

    // Wire up FeatureToggleService to Input
    if (this.modules.input) {
      this.modules.input.setFeatureToggleService(this.services.featureToggle);
    }

    this.services.headerUpgrade = new HeaderUpgradeController({
      button: document.getElementById('header-upgrade-btn'),
      eventBus,
      featureToggleService: this.services.featureToggle
    });
    this.services.headerUpgrade.initialize();

    // Initialize UsageService for daily-message-quota UI (Basic-tier users).
    // Premium/Guest/Discovery sessions get `unlimited: true` and render nothing.
    this.services.usage = new UsageService(eventBus, this.currentUser);
    this.services.usage.initialize();
    window.usageService = this.services.usage;
    console.log('✅ UsageService initialized');

    // Initialize AnalyticsService for GA4 tracking
    this.services.analytics = new AnalyticsService(eventBus, {
      userId: this.currentUser?.id,
      isGuest: this.isGuest,
      subscriptionTier: resolveAnalyticsTier(this.currentUser),
      onboardingComplete: this.currentUser?.onboardingStep === 'complete'
    });
    this.services.analytics.initialize();
    window.analyticsService = this.services.analytics;
    console.log('✅ AnalyticsService initialized');

    // Now initialize HeaderManager with ModeOrchestrator
    if (this.modules.headerManager) {
      this.modules.headerManager.modeOrchestrator = this.modules.modeOrchestrator;
      this.modules.headerManager.initialize();

      // Make globally accessible for dynamic text updates
      window.headerManager = this.modules.headerManager;
      console.log('✅ HeaderManager initialized and exported globally');
    }

    // Initialize DashboardManager with current user data
    this.modules.dashboardManager = new DashboardManager(eventBus, null, this.currentUser);

    // Initialize and display dashboard (replaces welcome message)
    this.modules.dashboardManager.initialize();

    // Initialize Section Controllers
    this.initializeSections();

    // Register sections with PageManager so it can deactivate the outgoing
    // section on navigation. Keys match `pageId` values in PageManager.pages.
    // Chat intentionally has no entry — ChatManager lifecycle is separate.
    if (this.modules.pageManager) {
      const sectionRegistry = {};
      const sectionsByPageId = {
        practices: this.modules.practicesSection,
        insights: this.modules.insightsSection,
        upgrade: this.modules.upgradeSection,
        subscription: this.modules.subscriptionSection,
        discovery: this.modules.discoverySection,
        partner: this.modules.partnerSection,
        notes: this.modules.partnerNotesSection,
        'account-settings': this.modules.accountSettingsSection,
        help: this.modules.helpSection,
        memory: this.modules.memorySection,
        search: this.modules.sessionsSection
      };
      for (const [pageId, section] of Object.entries(sectionsByPageId)) {
        if (section) sectionRegistry[pageId] = section;
      }
      this.modules.pageManager.setPageSections(sectionRegistry);
    }

    // Wire up module-to-chat integration
    this.initializeModuleIntegration();
  }

  /**
   * Initialize component event handlers for unified response pipeline.
   * Listens for component:requested events from ResponseHandler and
   * creates/renders the appropriate components.
   */
  initializeComponentHandlers() {
    if (!eventBus) return;

    // Handle questionnaire-launcher component requests from unified response
    eventBus.on('component:requested', async ({ type, data }) => {
      console.log('[App] Component requested:', type, data);

      switch (type) {
        case 'questionnaire-launcher':
          await this.handleQuestionnaireLauncherComponent(data);
          break;

        case 'action-buttons':
          // Handle action buttons component
          if (window.actionButtons && data.buttons) {
            const buttonGroup = window.actionButtons.createButtonGroup(
              data.buttons,
              data.options || {}
            );
            this.modules.chatManager.messageList.addInteractiveComponent(buttonGroup, {
              type: 'buttons',
              data: data.options || {}
            });
          }
          break;

        default:
          console.warn('[App] Unknown component type:', type);
      }
    });

    // Handle artifact requests from unified response (e.g., insight artifacts)
    // Event format: { type: 'insight', data: { status, title, pathId, insightType, insightId } }
    eventBus.on('component:artifact-requested', async ({ type, data }) => {
      console.log('[App] Artifact requested:', type, data);

      switch (type) {
        case 'insight':
          await this.handleInsightArtifact(data);
          break;

        default:
          console.warn('[App] Unknown artifact type:', type);
      }
    });

    // Handle orientation questionnaire launch.
    eventBus.on('orientation:questionnaire-ready', async ({ questionnaire, pathMetadata }) => {
      console.log('[App] Orientation questionnaire ready:', questionnaire?.questionnaire?.id);
      await this.handleOrientationQuestionnaire({
        questionnaire,
        pathMetadata
      });
    });

    // Handle orientation account creation request
    // Shows the login overlay in registration mode
    eventBus.on('orientation:show-account-creation', ({ context, intent } = {}) => {
      console.log('[App] Showing account creation for orientation', context || 'default');
      // With an intent the click is continued (or restored) after the overlay
      // resolves; without one this is the plain ACCOUNT_PROMPT ask, whose
      // outcome OrientationMode handles through auth:account-created /
      // auth:conversion-dismissed.
      if (intent) {
        this.openConversionGate({ ...intent, context: context || intent.context });
      } else {
        this.showLoginOverlayForConversion(context);
      }
    });

    // Handle orientation UI state changes
    // Manages header visibility during orientation flow
    eventBus.on('orientation:ui-state', async ({ state }) => {
      console.log('[App] Orientation UI state:', state);

      if (!this.modules.pageManager) {
        console.warn('[App] PageManager not available for orientation UI state');
        return;
      }

      switch (state) {
        case 'initial':
          // Clean orientation state: no header, no dashboard
          this.modules.pageManager.setOrientationInitialState();
          break;

        case 'chat':
          // Transition to normal chat (header slides down)
          this.modules.pageManager.transitionOrientationToChat();
          break;

        default:
          console.warn('[App] Unknown orientation UI state:', state);
      }
    });
  }

  /**
   * Handle insight artifact from unified response.
   * Creates InsightLauncher and adds it to chat.
   * Called by OrientationMode via component:artifact-requested event.
   * @param {Object} data - Artifact data from backend
   * @param {string} data.status - 'ready'
   * @param {string} data.title - Display title for the insight
   * @param {string} [data.pathId] - Path UUID that generated this insight
   * @param {string} data.insightType - 'individual' or 'couples'
   * @param {string|null} data.insightId - Insight UUID
   */
  async handleInsightArtifact(data) {
    const { InsightLauncher } = await import('./components/InsightLauncher.js');
    const pathId = data.pathId;

    // Generate temporary ID for tracking if not provided
    const trackingId = data.insightId || `${pathId}-${data.insightType}`;

    // Create insight object expected by InsightLauncher
    // Pass through all available fields from the artifact data
    const insight = {
      id: trackingId,
      status: data.status,
      title: data.title,
      content: data.content,
      pathId,
      insightType: data.insightType || 'individual',
      viewedAt: data.viewedAt,
      createdAt: data.createdAt,
      insightCode: data.insightCode
    };

    const insightLauncher = new InsightLauncher(insight, eventBus);

    // Add artifact to chat
    this.modules.chatManager.messageList.addInteractiveComponent(
      insightLauncher.render(),
      {
        type: 'insight',
        data: {
          insightId: trackingId,
          pathId,
          insightType: data.insightType
        }
      }
    );

    console.log('[App] Rendered insight artifact:', trackingId, 'state:', data.status);

    // Auto-open panel if requested (e.g., when discussing a specific insight)
    if (data.autoOpen && data.status === 'ready') {
      setTimeout(() => {
        console.log('[App] Auto-opening insight panel for:', trackingId);
        insightLauncher.openPanel();
      }, 500); // 500ms delay for smooth UX after message renders
    }
  }

  /**
   * Handle questionnaire-launcher component from unified response.
   * Creates QuestionnaireLauncher and adds it to chat.
   *
   * @param {Object} data - Component data from backend
   * @param {Object} data.questionnaire - Questionnaire data
   * @param {Object} data.pathMetadata - Path metadata
   */
  async handleQuestionnaireLauncherComponent(data) {
    const questionnaire = data.questionnaire;
    const pathMetadata = data.pathMetadata;

    if (!questionnaire || !pathMetadata) {
      console.error('[App] Invalid questionnaire-launcher data:', data);
      return;
    }

    const chatManager = this.modules.chatManager;

    // Create QuestionnaireLauncher with callbacks using unified flow
    const launcher = new QuestionnaireLauncher({
      questionnaireData: questionnaire,
      pathMetadata,
      eventBus: eventBus,
      onSubmit: async (responses, deactivatedQuestionIds) => {
        return await chatManager.handleQuestionnaireSubmit(
          questionnaire.questionnaire?.id || questionnaire.id,
          responses,
          deactivatedQuestionIds
        );
      },
      onComplete: async (completionData) => {
        return await chatManager.handleQuestionnaireComplete(
          pathMetadata.id,
          pathMetadata,
          completionData
        );
      },
      onStateChange: (newState, oldState) => {
        console.log(`[App] Questionnaire launcher state: ${oldState} → ${newState}`);
        if (newState === 'completed') {
          chatManager.currentActiveQuestionnaire = null;
        }
      }
    });

    // Add launcher to chat
    chatManager.messageList.addInteractiveComponent(launcher.render(), {
      type: 'questionnaire-launcher',
      data: {
        pathId: pathMetadata.id,
        pathMetadata,
        questionnaireId: questionnaire.questionnaire?.id || questionnaire.id
      }
    });

    // Store launcher reference
    chatManager.currentActiveQuestionnaire = {
      launcher: launcher,
      metadata: {
        pathId: pathMetadata.id,
        pathMetadata,
        questionnaireId: questionnaire.questionnaire?.id || questionnaire.id,
      }
    };

    console.log('[App] Questionnaire launcher added to chat');
  }

  /**
   * Handle orientation questionnaire launch.
   * Creates a QuestionnaireLauncher for the orientation flow with
   * orientation-specific completion handling.
   *
   * @param {Object} data - Questionnaire data
   * @param {Object} data.questionnaire - Full questionnaire data from backend
   * @param {Object} data.pathMetadata - Path metadata (may be minimal for orientation)
   */
  async handleOrientationQuestionnaire(data) {
    const { questionnaire } = data;
    const pathMetadata = data.pathMetadata;

    if (!questionnaire) {
      console.error('[App] Invalid orientation questionnaire data:', data);
      return;
    }

    const chatManager = this.modules.chatManager;

    // Handle both nested and flat questionnaire structures
    // Backend returns: { id, name, description, estimatedMinutes, questions }
    // QuestionnaireLauncher expects: { questionnaire: { id, name, ... }, questions: [...] }
    const questionnaireData = questionnaire.questionnaire
      ? questionnaire  // Already has nested structure
      : {
        questionnaire: {
          id: questionnaire.id,
          name: questionnaire.name,
          description: questionnaire.description,
          estimatedMinutes: questionnaire.estimatedMinutes
        },
        questions: questionnaire.questions || [],
        questionCount: questionnaire.questionCount
          ?? (questionnaire.questions || []).filter(q => q.questionType !== 'INSTRUCTION_CARD').length
      };

    const questionnaireId = questionnaireData.questionnaire.id;

    // Build orientation-specific path metadata
    const orientationMetadata = {
      id: pathMetadata?.id || 'orientation',
      name: pathMetadata?.name || 'Orientation',
      phase: 'orientation',
      ...pathMetadata
    };

    // Create QuestionnaireLauncher with orientation-specific callbacks
    const launcher = new QuestionnaireLauncher({
      questionnaireData: questionnaireData,
      pathMetadata: orientationMetadata,
      eventBus: eventBus,
      onSubmit: async (responses, deactivatedQuestionIds) => {
        // Use orientation-specific submit (no session required)
        // Clinical frameworks are scored and insights created inline by processBatchResponses
        const { OrientationFlowClient } = await import('./services/OrientationFlowClient.js');
        const flowClient = new OrientationFlowClient();

        try {
          const result = await flowClient.submitQuestionnaire({
            questionnaireId,
            responses,
            deactivatedQuestionIds
          });
          // Store submit result for onComplete to access
          launcher._orientationSubmitResult = result;
          return result;
        } catch (error) {
          console.error('[App] Orientation questionnaire submit failed:', error);
          throw error;
        }
      },
      onComplete: async (completionData) => {
        console.log('[App] Orientation questionnaire completed:', questionnaireId);

        // Insight was already created by processBatchResponses during submit
        const submitResult = launcher._orientationSubmitResult;

        // Emit completion event with insight data from submit response
        eventBus.emit('questionnaire:completed', {
          questionnaireId,
          pathId: orientationMetadata.id,
          pathMetadata: orientationMetadata,
          insightId: submitResult?.narrativeInsightId || null,
          narrativeContent: submitResult?.narrativeContent || null,
          narrativeDisplayLabel: submitResult?.narrativeDisplayLabel || null,
          narrativeDisplayItems: submitResult?.narrativeDisplayItems || null,
          narrativeDetailContent: submitResult?.narrativeDetailContent || null,
          classificationCode: submitResult?.classificationCode || null,
          scoreResult: submitResult?.scoreResult || null,
          insightStatus: submitResult?.narrativeInsightId ? 'ready' : 'no_insight'
        });
      },
      onStateChange: (newState, oldState) => {
        console.log(`[App] Orientation questionnaire state: ${oldState} → ${newState}`);
        if (newState === 'completed') {
          chatManager.currentActiveQuestionnaire = null;
        }
      }
    });

    // Add launcher to chat
    chatManager.messageList.addInteractiveComponent(launcher.render(), {
      type: 'questionnaire-launcher',
      data: {
        pathId: orientationMetadata.id,
        pathMetadata: orientationMetadata,
        questionnaireId: questionnaireId,
        isOrientation: true
      }
    });

    // Store launcher reference
    chatManager.currentActiveQuestionnaire = {
      launcher: launcher,
      metadata: {
        pathId: orientationMetadata.id,
        pathMetadata: orientationMetadata,
        questionnaireId: questionnaireId,
        isOrientation: true
      }
    };

    console.log('[App] Orientation questionnaire launcher added to chat');
  }

  /**
   * Open the conversion overlay on behalf of a specific guest action, so the
   * click can be continued after conversion or restored after a dismissal.
   *
   * This is the single entry point for every conversion gate. The clicked
   * action has already been recorded and its button group already torn down by
   * `executeAction` — dismissal is what re-arms it.
   *
   * @param {{action: string, context: string, groupId: (string|null), actionData: (Object|null)}} intent
   * @returns {Promise<void>}
   */
  async openConversionGate(intent) {
    this._conversionIntent = intent || null;
    await this.showLoginOverlayForConversion(intent?.context);
  }

  /**
   * Show login overlay configured for guest-to-account conversion.
   * Preserves orientation state and continues onboarding after conversion.
   *
   * @param {string} [context] - Which moment triggered the gate; selects the copy
   */
  async showLoginOverlayForConversion(context = null) {
    const loginOverlay = document.getElementById('login-overlay');

    if (loginOverlay) {
      loginOverlay.hidden = false;

      // Initialize auth manager if not already done
      if (!this.modules.authManager) {
        this.modules.authManager = new AuthManager(this);
      }
      await this.modules.authManager.initialize();

      // Start the decorative typewriter animation
      startLoginTypewriter();

      // Mark that we're in conversion mode
      this._isConvertingGuestToAccount = true;

      // Name the moment. A generic "Sign in" here would read as an interruption;
      // the whole design is that the ask lands where the value was just shown.
      this.modules.authManager.applyConversionCopy(
        CONVERSION_OVERLAY_COPY[context] || CONVERSION_OVERLAY_COPY.default
      );

      // A rejected access code captured at load time is the reason the account
      // step is about to fail — say so here rather than letting them find out
      // at submit.
      const pendingMessage = this._consumePendingLoginMessage();
      if (pendingMessage) {
        this.modules.authManager.showBetaAccessPrompt(pendingMessage);
      }
    }
  }

  /**
   * Continue the gated action now that the account exists.
   *
   * Handler-only: `executeAction` already recorded the click, echoed it, and
   * tore the group down when the gate opened, so re-entering it would
   * double-record the one interaction the user actually made.
   *
   * The intent is held until the continuation resolves, so a failure is
   * retryable rather than lost. If it fails anyway, land them somewhere real —
   * they *are* signed in, and a bare error toast on a buttonless screen is the
   * worst possible first second of a new account.
   *
   * @returns {Promise<void>}
   * @private
   */
  async _consumeConversionIntent() {
    const intent = this._conversionIntent;
    if (!intent?.action) return;

    try {
      const router = this.modules.modeOrchestrator?.actionRouter;
      if (!router?.runHandlerOnly) throw new Error('ActionRouter unavailable for continuation');
      await router.runHandlerOnly(intent.action, intent.actionData || {});
      this._conversionIntent = null;
    } catch (error) {
      console.error('[App] Conversion continuation failed:', error);
      this._conversionIntent = null;
      window.notificationManager?.showInfo?.("You're all set — your account is ready.");
      page('/');
    }
  }

  /**
   * Resolve a dismissed conversion gate. Semantics are per-action: some restore
   * what was torn down, and one (the demo's "I'm done for now") proceeds as a
   * guest — asking twice in one session is the wrong kind of pushy, and the
   * dashboard picks the thread back up.
   *
   * @returns {Promise<boolean>} True if an intent was handled here
   * @private
   */
  async _resolveDismissedConversionIntent() {
    const intent = this._conversionIntent;
    if (!intent?.action) return false;
    this._conversionIntent = null;

    const router = this.modules.modeOrchestrator?.actionRouter;

    try {
      if (intent.action === 'orientation_exit') {
        // Declining here means declining the account, not the exit.
        await router?.runHandlerOnly?.('orientation_exit', intent.actionData || {});
        return true;
      }
      // Everything else restores the affordance the click tore down, so the
      // conversation is exactly where they left it — no replayed copy. Modes
      // whose controls aren't a registered button group (orientation renders
      // its own) supply their own restore.
      if (typeof intent.restore === 'function') {
        await intent.restore();
      } else if (intent.groupId) {
        await router?.rearmButtonGroup?.(intent.groupId, {});
      }
      return true;
    } catch (error) {
      console.error('[App] Conversion dismissal handling failed:', error);
      return true;
    }
  }

  /**
   * Handle successful guest-to-account conversion.
   * Called by AuthManager after any in-place registration of a guest — from
   * orientation's account prompt, the dashboard's conversion row, or a guest
   * gate mid-chat. Emits auth:account-created for OrientationMode and GA4.
   *
   * @param {Object} userData - The newly created user data
   * @returns {Promise<void>}
   */
  async handleGuestConversion(userData) {
    console.log('[App] Guest converted to account:', userData?.username);

    // Update current user
    this.currentUser = userData;
    this.isGuest = false;

    // Conversion keeps the same user row, so in-memory state is already
    // correct — re-seed from the response anyway so every auth outcome ends
    // with hint state derived from the server rather than from what the page
    // happened to accumulate.
    helpHintService.hydrate(userData?.helpHints);

    // Update analytics service with new user data
    if (this.services.analytics) {
      this.services.analytics.setUser(userData);
    }

    // `user:updated` is the single re-arm signal for everything that keyed off
    // `isGuest` at boot: FeatureToggleService re-resolves sidebar/content
    // visibility from `hasContent` (the demo session's own output), and
    // UsageService, BadgeService and HeaderUpgradeController run the
    // requireAuth fetches a guest boot skipped. Emit BEFORE any continuation
    // runs — a continuation that switches mode must not race the resolve, or a
    // brand-new account renders as empty for the first beat of its first real
    // screen. `EventBus.emit` is synchronous, so every subscriber has run by
    // the next line. There is no /auth/me refetch on this path; do not call
    // any of the four services directly.
    eventBus.emit('user:updated', { user: userData });

    // Hide login overlay
    this.hideLoginOverlay();

    // Reset conversion flag
    this._isConvertingGuestToAccount = false;

    // The sidebar profile button and the dashboard's synthetic rows are pure
    // functions of `isGuest`, and neither re-derives itself. Without this the
    // button keeps its signed-out onclick (which hides the app behind the
    // sign-in wall) and the "Create your free account" row outlives the
    // conversion it invited. The dashboard refresh is deliberately not
    // awaited: it fetches account activity under a 10s timeout, and the
    // continuation below (orientation's next beat, or a gated action) must
    // not wait on dashboard networking. `loadRecentActivity` catches its own
    // errors, so the dangling promise never rejects.
    this.displayUserInfo();
    this.modules.dashboardManager?.refreshRecentActivity();

    // Emit event for OrientationMode to continue (and for GA4's account_created,
    // which reports this one as from_guest:'yes'). Guest conversion is always the
    // email verify-code flow — a guest who signs in with Google is converted by
    // passport and returns through checkAuth, never through here.
    this.noteAccountCreated({ user: userData, method: 'email', fromConversion: true });

    // Continue whatever the guest clicked to get here (if anything).
    await this._consumeConversionIntent();
  }

  /**
   * Initialize section controllers
   */
  initializeSections() {
    // Practices Section
    const practicesContainer = document.getElementById('practices-page');
    if (practicesContainer) {
      this.modules.practicesSection = new PracticesSection({
        container: practicesContainer
      }, eventBus);
    }

    // Insights Section (was Understand)
    const insightsContainer = document.getElementById('insights-page');
    if (insightsContainer) {
      this.modules.insightsSection = new InsightsSection({
        container: insightsContainer
      }, eventBus);
    }

    // Upgrade Section
    const upgradeContainer = document.getElementById('upgrade-page');
    if (upgradeContainer) {
      this.modules.upgradeSection = new UpgradeSection({
        container: upgradeContainer
      }, eventBus);
    }

    // Subscription Management Section (Premium users)
    const subscriptionContainer = document.getElementById('subscription-page');
    if (subscriptionContainer) {
      this.modules.subscriptionSection = new SubscriptionSection({
        container: subscriptionContainer
      }, eventBus);
    }

    // Sessions Section (session notes, rendered on the Search page)
    const searchContainer = document.getElementById('search-page');
    if (searchContainer) {
      this.modules.sessionsSection = new SessionsSection({
        container: searchContainer
      }, eventBus);
    }

    // Discovery Section (was Share)
    const discoveryContainer = document.getElementById('discovery-page');
    if (discoveryContainer) {
      this.modules.discoverySection = new DiscoverySection({
        container: discoveryContainer
      }, eventBus, this.currentUser?.coupleId, this.currentUser);
    }

    // Profile-menu placeholder sections (Partner, Account & settings, Help)
    const partnerContainer = document.getElementById('partner-page');
    if (partnerContainer) {
      this.modules.partnerSection = new PartnerSection({
        container: partnerContainer
      }, eventBus);
    }

    // Notes Section (couple-gated notes inbox)
    const partnerNotesContainer = document.getElementById('notes-page');
    if (partnerNotesContainer) {
      this.modules.partnerNotesSection = new PartnerNotesSection({
        container: partnerNotesContainer
      });
    }

    const accountSettingsContainer = document.getElementById('account-settings-page');
    if (accountSettingsContainer) {
      this.modules.accountSettingsSection = new AccountSettingsSection({
        container: accountSettingsContainer
      }, eventBus);
    }

    const helpContainer = document.getElementById('help-page');
    if (helpContainer) {
      this.modules.helpSection = new HelpSection({
        container: helpContainer
      }, eventBus);
    }

    const memoryContainer = document.getElementById('memory-page');
    if (memoryContainer) {
      this.modules.memorySection = new MemorySection({
        container: memoryContainer
      }, eventBus);
    }
  }

  /**
   * Initialize module-to-chat integration
   * Wires up events for page management and right panel
   *
   * Note: Module selection events are handled by ModeOrchestrator,
   * which routes questionnaire loading through the proper mode system.
   */
  initializeModuleIntegration() {
    if (!eventBus) return;

    // Remove existing listeners
    eventBus.off('dashboard:navigate');
    eventBus.off('dashboard:activity-selected');
    eventBus.off('message:send');
    // NOTE: Do NOT remove page:changed - PageManager still emits it

    // Dashboard navigation card clicks
    eventBus.on('dashboard:navigate', async ({ mode, cardId }) => {
      console.log('Dashboard navigation:', mode, cardId);
      eventBus.emit('navigate', { page: 'chat', mode });
    });

    // Dashboard activity item clicks
    eventBus.on('dashboard:activity-selected', async ({ item }) => {
      console.log('Dashboard activity selected:', item);

      if (item.type === 'guest_conversion') {
        // The dashboard's standing conversion offer. No intent to continue —
        // clicking it IS the whole action, so the overlay closes back to the
        // dashboard on dismissal.
        await this.showLoginOverlayForConversion('dashboard');
      } else if (item.type === 'path') {
        // Navigate to chat in DISCOVERY mode with the selected path.
        eventBus.emit('navigate', {
          page: 'chat',
          mode: 'DISCOVERY',
          pathId: item.metadata.pathId,
          pathMetadata: item.metadata.pathMetadata || {
            id: item.metadata.pathId,
            name: item.title
          }
        });
      } else if (item.type === 'guide') {
        // Guides live inside InsightsSection (the Library page). URL
        // drives tab + detail panel directly — no redirect needed.
        page(`/library?tab=guides&guide=${encodeURIComponent(item.metadata.guideDefinitionId)}`);
      } else if (item.type === 'sharing' || item.type === 'partner_invite') {
        // Two partner surfaces, one destination. `sharing` is the gestalt
        // consent nudge (review and approve what you share); `partner_invite`
        // is the pre-link nudge (create and send an invitation). /partner
        // renders whichever state applies, so both route via metadata.route.
        page(item.metadata?.route || '/partner');
      } else if (item.type === 'partner_notes') {
        // The partner-notes introduction. Routes to /notes with ?info=1 so the
        // About Notes panel opens on arrival — the row's whole job is to explain
        // the feature, so landing on an empty list would answer nothing.
        page(item.metadata?.route || '/notes?info=1');
      } else if (item.type === 'practice') {
        // Open the practice check-in directly in chat (TREATMENT/checkin),
        // mirroring PracticesSection.handleDiscuss — rather than routing to the
        // /practices detail view. Uses the practice UUID, not practiceNumber.
        eventBus.emit('navigate', {
          page: 'chat',
          mode: 'TREATMENT',
          subMode: 'checkin',
          practiceId: item.metadata.practiceId
        });
      } else if (item.type === 'active_advice_session') {
        // Resume the still-ACTIVE Advice session (user is on the dashboard,
        // reopened the browser, or switched devices). Reuses the proven
        // resumeSession path — loads prior messages into the chat, no new session.
        const sessionId = item.metadata?.sessionId;
        if (!sessionId) return;
        // Await the page swap and bail if PageManager superseded us, then push
        // `/chat` so the address bar and Back button match every other
        // dashboard-launched chat entry (intakeIssueLauncher, memoEntryLauncher).
        const swapped = await this.modules.pageManager?.showPage('chat');
        if (swapped === false) return;
        window.history.pushState({}, '', '/chat');
        try {
          await this.modules.modeOrchestrator.resumeSession('ADVICE', sessionId);
        } catch (err) {
          // Session may have closed between dashboard load and click — start fresh.
          console.error('[App] Failed to resume advice session:', err?.message || err);
          window.notificationManager?.showInfo?.(
            "Couldn't resume that conversation — starting a new one.", 6000
          );
          eventBus.emit('navigate', { page: 'chat', mode: 'ADVICE' });
        }
      } else if (item.type === 'intake_issue') {
        // Carry the concern scoped at intake into a seamless ADVICE session via
        // the shared launcher (NOT resumeSession — this creates a fresh
        // intake_issue session; `force:true` inside the launcher matters most
        // here since the dashboard is reachable mid-advice-session).
        const sourceSessionId = item.metadata?.sourceSessionId;
        if (!sourceSessionId) return;
        const { launchIntakeIssueAdvice } = await import('./orchestration/intakeIssueLauncher.js');
        await launchIntakeIssueAdvice({ sourceSessionId, title: item.title });
      } else if (item.type === 'orientation') {
        // Resume a dismissed orientation. Clears `orientationDismissedAt`
        // server-side and locally, then re-enters ORIENTATION mode from the
        // saved phase. Router's mode-based check force-routes to chat.
        // `resumeFrom` / `onboardingData` are passed at the top level — they
        // flow through the generic switchMode path (ModeOrchestrator
        // handleNavigation @ line ~1026) which spreads navData directly into
        // OrientationMode.onEnter's transitionData arg.
        try {
          const { API } = await import('./core/api.js');
          await API.resumeGuestOnboarding();
          if (this.currentUser) {
            this.currentUser.orientationDismissedAt = null;
          }
        } catch (err) {
          console.error('[App] Failed to clear orientation dismissal:', err);
          // Continue navigation regardless — server state reconciles on reload.
        }
        eventBus.emit('navigate', {
          page: 'chat',
          mode: 'ORIENTATION',
          resumeFrom: this.currentUser?.onboardingStep || null,
          onboardingData: this.currentUser?.onboardingData || {}
        });
      }
    });

    // Message send - ensure chat page is visible
    // Session creation handled by ModeOrchestrator.handleUserMessage()
    eventBus.on('message:send', async () => {
      if (this.modules.pageManager?.isDashboardVisible()) {
        this.modules.pageManager.showPage('chat');
        // Clear return session - user is starting fresh interaction
        this.returnSession = null;
      }
    });

  }

  /**
   * Initialize UI components
   */
  initializeComponents() {
    // Initialize action buttons component
    this.actionButtons = new ActionButtons();
    // Make globally accessible for ModeOrchestrator
    window.actionButtons = this.actionButtons;
    console.log('ActionButtons initialized and available globally');

    // Privacy state owner; header entry/exit is dispatched by ChatHeaderMenu.
    this.modules.privacyModeManager = new PrivacyModeManager();
    window.privacyModeManager = this.modules.privacyModeManager;
    console.log('PrivacyModeManager initialized and available globally');

    // Initialize orientation close manager (mirrors PrivacyModeManager pattern:
    // long-lived header button, click handler bound once, visibility driven by
    // FeatureToggleService via toggle:closeButton).
    this.modules.orientationCloseManager = new OrientationCloseManager({
      closeButton: document.getElementById('header-close-btn')
    });
    window.orientationCloseManager = this.modules.orientationCloseManager;
    console.log('OrientationCloseManager initialized and available globally');

    // Initialize profile popup (sidebar account menu)
    this.modules.profilePopup = new ProfilePopup();

    // The sidebar badge renders before the shared subscription snapshot lands
    // (UpgradeService kicks that fetch at bootstrap), so repaint it on arrival.
    eventBus.on('subscription:status', () => this.updateSidebarProfileButton());

    // Profile popup menu → sign out
    eventBus.on('profile:signout', () => this.handleLogout());

    this.modules.chatHeaderMenu = new ChatHeaderMenu({
      container: document.getElementById('header-options-mount'),
      privacyModeManager: this.modules.privacyModeManager,
      modeOrchestrator: this.modules.modeOrchestrator,
      app: this,
      featureToggleService: this.services.featureToggle
    });
    window.chatHeaderMenu = this.modules.chatHeaderMenu;

    // Header remaining-work button → path progress breakdown. Reads the live
    // discovery mode at open time, so it needs no wiring to the header pipeline.
    this.modules.pathProgressPopover = new PathProgressPopover({
      trigger: document.getElementById('header-remaining'),
      discoveryAPI: new DiscoveryAPI()
    });

    // Wire up FeatureToggleService to OrientationCloseManager
    if (this.services.featureToggle && this.modules.orientationCloseManager) {
      this.modules.orientationCloseManager.setFeatureToggleService(this.services.featureToggle);
    }

    // Initialize button group registry
    const buttonGroupRegistry = new ButtonGroupRegistry();
    window.buttonGroupRegistry = buttonGroupRegistry;

    // Load button groups asynchronously
    buttonGroupRegistry.load().then(success => {
      if (success) {
        console.log('Button groups loaded successfully');
      } else {
        console.warn('Failed to load button groups - show_buttons tool will not work');
      }
    });
  }

  /**
   * Initialize navigation and event listeners
   */
  initializeNavigation() {
    // Unified handler for all sidebar navigation items
    const sidebarItems = document.querySelectorAll('.sidebar__item[data-action]');
    console.log(`[App] Found ${sidebarItems.length} sidebar items with data-action`);

    sidebarItems.forEach(item => {
      item.addEventListener('click', async (e) => {
        const action = item.getAttribute('data-action');
        console.log(`[App] Sidebar item clicked:`, action);

        // Handle different action types via eventBus navigation
        switch (action) {
          case 'dashboard': {
            // "New chat" owns the dashboard, so it renders as the active
            // sidebar item while the dashboard is showing (see
            // PageManager.updateSidebarActiveState).
            const alreadyOnDashboard = this.modules.pageManager?.isDashboardActive?.() === true;

            // Fully clear mode so router shows dashboard (not previous chat)
            this.modules.modeOrchestrator?.returnToDashboard();
            this.returnSession = null;

            // Clicking the active item is inert: re-routing to '/' would re-run
            // the base route and visibly reset the dashboard for no gain. The
            // state clearing above still runs, so the intent ("forget the last
            // chat") is honoured even though nothing moves.
            if (alreadyOnDashboard) break;

            eventBus.emit('navigate', { page: 'dashboard' });
            break;
          }

          case 'advice':
            // Check for session restoration
            if (this.returnSession?.mode === 'ADVICE') {
              // Restore existing ADVICE session
              console.log('[App] Sidebar: Restoring existing ADVICE session');
              eventBus.emit('navigate', { page: 'chat', mode: 'ADVICE', restoreSession: true });
            } else {
              // Create new ADVICE session
              console.log('[App] Sidebar: Creating new ADVICE session');
              eventBus.emit('navigate', { page: 'chat', mode: 'ADVICE' });
            }
            break;

          case 'page':
            const targetPage = item.getAttribute('data-page');
            if (targetPage) {
              // Special case: clicking Chat while on chat page goes to dashboard
              if (targetPage === 'chat' && this.modules.pageManager?.isPageActive('chat')) {
                // Clear mode display (header) while preserving session
                this.modules.modeOrchestrator?.clearModeDisplay();
                // Navigate through router - it saves session and shows dashboard
                eventBus.emit('navigate', { page: 'chat' });
                break;
              }

              // Map internal page names to eventBus page identifiers
              // Maps the sidebar button's `data-page` attribute to the
              // eventBus 'navigate' page identifier — identity for every
              // current entry, but kept as an explicit map (not a passthrough)
              // since historically some HTML/eventBus page names diverged.
              const pageMap = {
                'assessments': 'assessments',
                'practices': 'practices',
                'insights': 'insights',
                'chat': 'chat',
                'discovery': 'discovery',
                'notes': 'notes',
                'bridge': 'bridge',
                'search': 'search'
              };
              const navPage = pageMap[targetPage] || 'chat';
              eventBus.emit('navigate', { page: navPage });
            }
            break;

          default:
            console.warn(`Unknown sidebar action: ${action}`);
        }
      });
    });

    // Logo click handler. The logo is only shown (and interactive) when the
    // sidebar is pinned — there it navigates home. Guard for the collapsed
    // case anyway: if clicked while collapsed, expand instead of navigating.
    // On mobile also close the overlay — the logo is not a .sidebar__item, so
    // the sidebar's own auto-close handler doesn't cover it.
    const logoHome = document.getElementById('logo-home');
    if (logoHome) {
      logoHome.addEventListener('click', () => {
        if (this.modules.sidebar && !this.modules.sidebar.isPinned()) {
          this.modules.sidebar.toggle();
          return;
        }
        console.log('[App] Logo clicked, navigating to dashboard');
        // Fully clear mode so router shows dashboard (not previous chat)
        this.modules.modeOrchestrator?.returnToDashboard();
        this.returnSession = null;
        eventBus.emit('navigate', { page: 'dashboard' });
        eventBus.emit('sidebar:close-mobile');
      });
    }

  }

  /**
   * Get current section from URL or state
   * @returns {string|null} Current section name
   */
  getCurrentSection() {
    // This can be enhanced to read from URL params or application state
    // For now, returning null - can be implemented based on routing logic
    return null;
  }


  /**
   * Display user information in the UI
   */
  displayUserInfo() {
    const usernameElement = document.getElementById('current-username');
    if (usernameElement && this.currentUser) {
      // Don't display guest-style usernames
      const displayUsername = isGuestName(this.currentUser.username) ? '' : this.currentUser.username;
      usernameElement.textContent = displayUsername;
    }

    // Update sidebar profile button based on auth state
    this.updateSidebarProfileButton();
  }

  /**
   * The plan the sidebar badge should show.
   *
   * The shared snapshot wins whenever one exists: it is refetched, and
   * `currentUser.subscriptionTier` is not — it is stamped once by the identity
   * response (`/auth/me`, `/login`, `/register/verify`) and would otherwise
   * pin the badge to its boot-time value through an in-app upgrade. That
   * field is the pre-snapshot fallback only.
   *
   * `devSubscriptionTier` is written exclusively by `dev.profile.*` and takes
   * absolute precedence — a deliberate preview override must survive whatever
   * the real account's snapshot says.
   *
   * @returns {'basic'|'premium'} Plan discriminator
   * @private
   */
  _resolveSidebarPlan() {
    const devOverride = this.currentUser?.devSubscriptionTier;
    if (devOverride) return isPaidPlan(devOverride) ? devOverride : 'basic';

    const snapshot = getSubscriptionSnapshot();
    if (snapshot) return planFromStatus(snapshot);

    const tier = this.currentUser?.subscriptionTier;
    return isPaidPlan(tier) ? tier : 'basic';
  }

  /**
   * Update sidebar profile button based on authentication and subscription state
   */
  updateSidebarProfileButton() {
    const profileBtn = document.getElementById('sidebar-profile-btn');
    const avatarContainer = document.getElementById('sidebar-profile-avatar');
    const nameElement = document.getElementById('sidebar-profile-name');
    const tierElement = document.getElementById('sidebar-profile-tier');
    const upgradeBtn = document.getElementById('sidebar-profile-upgrade');

    if (!profileBtn) return;

    const isSignedIn = this.currentUser && !this.isGuest;
    const plan = this._resolveSidebarPlan();
    const isPaid = isPaidPlan(plan);

    if (isSignedIn) {
      // User is signed in - show avatar, name, and account type
      profileBtn.classList.add('sidebar__profile-btn--signed-in');
      profileBtn.setAttribute('data-tooltip', 'Account');
      profileBtn.setAttribute('aria-label', 'Account');

      // Update avatar
      if (avatarContainer) {
        const size = parseInt(avatarContainer.getAttribute('data-size'), 10) || 24;
        // Clear existing content but preserve user icon for CSS toggling
        const userIcon = avatarContainer.querySelector('.sidebar__profile-user-icon');
        avatarContainer.innerHTML = '';
        if (userIcon) {
          avatarContainer.appendChild(userIcon);
        }
        const avatar = createProfileAvatar(this.currentUser, { size });
        avatarContainer.appendChild(avatar);
      }

      // Update name
      if (nameElement) {
        const firstName = this.currentUser.firstName || this.currentUser.name || 'User';
        nameElement.textContent = firstName;
      }

      // Update plan badge
      if (tierElement) {
        tierElement.textContent = `${planName(plan)} Plan`;
      }

      // Upgrade button belongs to the Basic plan only — Premium has nothing
      // left to buy here.
      if (isPaid) {
        profileBtn.classList.remove('sidebar__profile-btn--show-upgrade');
      } else {
        profileBtn.classList.add('sidebar__profile-btn--show-upgrade');
      }

      // Signed-in: clicking the profile button toggles the profile popup menu
      profileBtn.onclick = (e) => {
        // Ignore clicks on the upgrade sub-button (it has its own handler)
        if (e.target.closest('#sidebar-profile-upgrade')) return;
        e.preventDefault();
        e.stopPropagation();
        if (this.modules.profilePopup) {
          this.modules.profilePopup.update(this.currentUser);
          this.modules.profilePopup.toggle();
        }
      };
    } else {
      // User is signed out (or guest) - show sign-in state
      profileBtn.classList.remove('sidebar__profile-btn--signed-in');
      profileBtn.classList.remove('sidebar__profile-btn--show-upgrade');
      profileBtn.setAttribute('data-tooltip', 'Sign In');
      profileBtn.setAttribute('aria-label', 'Sign In');

      // Reset avatar container to show user icon
      if (avatarContainer) {
        const userIcon = avatarContainer.querySelector('.sidebar__profile-user-icon');
        avatarContainer.innerHTML = '';
        if (userIcon) {
          avatarContainer.appendChild(userIcon);
        } else {
          // Recreate user icon if missing
          const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
          svg.classList.add('sidebar__profile-user-icon');
          svg.setAttribute('width', '28');
          svg.setAttribute('height', '28');
          const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
          use.setAttribute('href', '#icon-user');
          svg.appendChild(use);
          avatarContainer.appendChild(svg);
        }
      }

      // Show "Sign in" text
      if (nameElement) {
        nameElement.textContent = 'Sign in';
      }

      // Clear tier
      if (tierElement) {
        tierElement.textContent = '';
      }

      // Set up click to show login overlay
      profileBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.showLoginOverlay();
      };
    }

    // Set up upgrade button click handler
    if (upgradeBtn) {
      upgradeBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleUpgradeClick();
      };
    }
  }

  /**
   * Handle sidebar upgrade button click — emit the shared `upgrade:clicked`
   * analytics event and navigate to the upgrade page via the SPA router.
   */
  handleUpgradeClick() {
    console.log('[App] Upgrade button clicked');
    goToUpgrade(eventBus, 'sidebarButton');
  }

  /**
   * Initialize authentication-related UI elements
   */
  initializeAuth() {
    const logoutButton = document.getElementById('logout-button');
    if (logoutButton) {
      logoutButton.addEventListener('click', () => this.handleLogout());
    }
  }

  /**
   * Handle user logout
   */
  async handleLogout() {
    console.log('[App] Logging out...');

    try {
      const result = await API.logout();
      if (result) {
        console.log('[App] Logout successful');
      }
    } catch (error) {
      console.error('[App] Logout error:', error);
    } finally {
      // Always clean up and show login (even if logout API fails)
      this.cleanupBeforeLogout();
      this.showLoginOverlay();
    }
  }

  /**
   * Clean up app state before showing login
   */
  cleanupBeforeLogout() {
    // Tear down the active section first, while it can still reference its
    // dependencies. Stops observers, timers, and in-flight polling.
    this.modules.pageManager?.deactivateCurrent();

    // Clear user info
    this.currentUser = null;

    // Reset per-user UsageService state so counters/banners don't leak
    // across accounts on shared devices.
    this.services.usage?.reset?.();

    // Same reason: hint exposure counts and dismissals are per-user.
    helpHintService.reset();

    // Cancel any armed badge count refresh so it can't fire against the dead
    // session and 401 behind the login overlay.
    this.modules.badgeService?.destroy?.();

    // Clear session manager
    if (sessionManager) {
      sessionManager.resetSession();
    }

    // Clear return session
    this.returnSession = null;

    // Clear any pending navigation
    this.pendingNavigation = null;

    // Update sidebar profile button to signed-out state
    this.updateSidebarProfileButton();

    console.log('[App] State cleaned up for logout');
  }

  /**
   * Restore a checkout-return snapshot from sessionStorage on a fresh boot
   * after a Stripe Checkout roundtrip. Populates `this.checkoutReturnSession`
   * so the router's Priority 1.5 branch can pick it up and call
   * `ModeOrchestrator.resumeSession(mode, sessionId)`.
   *
   * Entries are consumed (removed) on read so a page refresh doesn't trigger
   * a spurious resume. Entries older than 15 minutes are discarded — real
   * Stripe flows are almost always <5 min; anything longer suggests the user
   * context-switched and shouldn't be yanked back into a cold conversation.
   *
   * @private
   */
  _rehydrateCheckoutReturnSession() {
    try {
      const raw = sessionStorage.getItem('app:checkoutReturnSession');
      if (!raw) return;
      sessionStorage.removeItem('app:checkoutReturnSession');

      const saved = JSON.parse(raw);
      if (!saved?.mode || !saved?.sessionId || !saved?.timestamp) return;

      const MAX_AGE_MS = 15 * 60 * 1000;
      if (Date.now() - saved.timestamp > MAX_AGE_MS) {
        console.log('[App] Checkout return session expired, ignoring');
        return;
      }

      this.checkoutReturnSession = saved;
      console.log('[App] Rehydrated checkout return session:', saved);
    } catch (err) {
      console.warn('[App] Failed to rehydrate checkout return session:', err?.message || err);
    }
  }
}

// Initialize app when DOM is ready
// Modules execute after DOM is loaded by default, but check to be safe
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

async function initApp() {
  // Load SVG sprite before app initialization
  await loadSvgSprite();

  // Installed before the app so a file dropped during startup cannot navigate
  // away mid-initialization.
  installFileDropGuard();

  window.app = new App();
}

// Export for debugging
export { App };
